import React, { useCallback, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Popover, Table } from 'antd-v5';
import { InfoCircleOutlined } from '@ant-design/icons';
import InquiryExpandedRow from './InquiryExpandedRow';
import useVendorByKey from '../../../../hooks/query/useVendorByKey';
import { isTerminalStatus } from '../utils';
import formatNumberAsCurrency from '../../../../utils/formatNumberAsCurrency';

const TOTAL_SPEND_INFO = (
  <div style={{ maxWidth: 320 }}>
    <strong>Total spend</strong> is this client&apos;s lifetime spend across all available purchases from your Point of
    Sale. It refreshes when you open the inquiry&apos;s detail row or the client&apos;s Rolex tab. See the client
    profile for a full purchase breakdown.
  </div>
);

const InquiryTable = ({ data, loading, onEdit, canEdit, page, pageSize, total, onPageChange, hasPos }) => {
  const history = useHistory();
  const { vendorId: rolexVendorId } = useVendorByKey('rolex');
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const handleExpandRow = useCallback((expanded, record) => {
    if (expanded) {
      setExpandedRowKeys(prev => [...prev, record.id]);
    } else {
      setExpandedRowKeys(prev => prev.filter(key => key !== record.id));
    }
  }, []);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'clientName',
      key: 'clientName',
      sorter: (a, b) => a.clientName.localeCompare(b.clientName),
      render: (text, record) => (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <span
          className="inquiry-cell-name"
          data-testid="inquiries__table--name"
          onClick={() => history.push(`/Clients?client=${record.clientId}&clientTab=rolex`)}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Inquiry Date',
      dataIndex: 'inquiryDate',
      key: 'inquiryDate',
      sorter: (a, b) => a.inquiryDate.localeCompare(b.inquiryDate),
      render: (text, record) => (
        <div className="inquiry-cell-date">
          <span className="inquiry-cell-date__value">{text}</span>
          <span className="inquiry-cell-date__duration">{record.durationMonths}</span>
        </div>
      ),
    },
    {
      title: 'Product',
      dataIndex: 'productName',
      key: 'productName',
      render: (text, record) => {
        const canNavigate = !!(rolexVendorId && record.productVendorItemId);
        return (
          <div className="inquiry-cell-product">
            {record.productImage && (
              <img className="inquiry-cell-product__image" src={record.productImage} alt={record.productMpn} />
            )}
            <div className="inquiry-cell-product__info">
              {canNavigate ? (
                <Link
                  to={`/Products?vendorId=${rolexVendorId}&productId=${record.productVendorItemId}`}
                  className="inquiry-cell-product__name"
                >
                  {text}
                </Link>
              ) : (
                <span style={{ fontSize: 14, color: '#000' }}>{text}</span>
              )}
              <span className="inquiry-cell-product__mpn">
                {record.productMpn}
                {record.productCount > 1 && (
                  <span className="inquiry-cell-product__more"> +{record.productCount - 1} more</span>
                )}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      title: (
        <span>
          Total Spend{' '}
          <Popover content={TOTAL_SPEND_INFO} trigger={['click', 'hover']}>
            <InfoCircleOutlined
              style={{ opacity: 0.45, marginLeft: 2, cursor: 'pointer' }}
              onClick={e => e.stopPropagation()}
            />
          </Popover>
        </span>
      ),
      dataIndex: 'totalSpend',
      key: 'totalSpend',
      sorter: (a, b) => (a.totalSpend ?? 0) - (b.totalSpend ?? 0),
      render: value => (value == null ? '' : formatNumberAsCurrency(value)),
      hidden: !hasPos,
    },
    {
      title: 'Last Inbound',
      dataIndex: 'lastInbound',
      key: 'lastInbound',
      sorter: (a, b) => a.lastInbound.localeCompare(b.lastInbound),
      render: (text, record) => (
        <div className="inquiry-cell-date">
          <span className="inquiry-cell-date__value">{text}</span>
          <span className="inquiry-cell-date__duration">{record.lastInboundDuration}</span>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: value => {
        const label = { WAITING: 'Waiting', ORDERED: 'Ordered', CANCELED: 'Canceled', FULFILLED: 'Fulfilled' };
        return label[value] || value;
      },
    },
    {
      title: '',
      key: 'edit',
      width: 60,
      render: (_, record) => {
        if (canEdit && !canEdit(record)) return null;
        return (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions
          <span
            className="inquiry-cell-edit"
            data-testid="inquiries__table--edit-button"
            onClick={() => onEdit(record)}
          >
            {isTerminalStatus(record.status) ? 'View' : 'Edit'}
          </span>
        );
      },
    },
  ];

  return (
    <div className="rolex-inquiries__table">
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        onRow={() => ({ 'data-testid': 'inquiries__table--row' })}
        expandable={{
          // eslint-disable-next-line react/no-unstable-nested-components
          expandedRowRender: record => <InquiryExpandedRow inquiry={record} />,
          expandedRowKeys,
          onExpand: handleExpandRow,
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: onPageChange,
          showSizeChanger: { showSearch: false },
          showTotal: t => `Total ${t} items`,
        }}
        size="middle"
      />
    </div>
  );
};

export default InquiryTable;
